import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import { v4 as uuidv4 } from 'uuid';
import md5 from 'crypto-js/md5';

import { sharedSalesEnabled } from '../../lib/crossCustomerSales';

@withText(props => ({
    pickerTitle: <Text id='calendar.todoPicker.title'>Attach customer and animal</Text>,
    attachTitle: <Text id='calendar.todos.attach'>Attach customer / animal</Text>,
    goToSaleTitle: <Text id='calendar.todos.go-to-sale'>Open sale</Text>,
    detachTitle: <Text id='calendar.todos.detach'>Remove link</Text>,
}))
@observer
class CalendarTodos extends Component {
    constructor(props) {
        super(props);
        this.state = {
            newTodoText: '',
            editingUuidv4: null,
            updateInProgress: {},
        };
        this.updateTimers = {};
    }

    componentWillUnmount() {
        Object.keys(this.updateTimers).forEach((key) => {
            clearTimeout(this.updateTimers[key]);
        });
    }

    setUpdateInProgress = (key, value) => {
        this.setState(prevState => ({
            updateInProgress: { ...prevState.updateInProgress, [key]: value },
        }));
    };

    autoGrow = (el) => {
        if (!el) {
            return;
        }
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    };

    textareaRef = (el) => {
        if (!el) {
            return;
        }
        this.autoGrow(el);
        if (!el.dataset.focused) {
            el.dataset.focused = '1';
            el.focus();
            const len = el.value.length;
            try {
                el.setSelectionRange(len, len);
            } catch (e) {
                // ignore — setSelectionRange not supported in some states
            }
        }
    };

    addTodo = async () => {
        const { calendar, stores } = this.props;
        const { calendarStore } = stores;
        const text = (this.state.newTodoText || '').trim();
        if (!text || !calendar?.id) {
            return;
        }
        this.setState({ newTodoText: '' });
        await calendarStore.addArrayObject({
            newObject: { uuidv4: uuidv4(), text, done: false },
            object: calendar,
            field: 'todos',
        });
    };

    handleNewTodoKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.addTodo();
        }
    };

    toggleDone = async (todo) => {
        const { calendar, stores } = this.props;
        const { calendarStore } = stores;
        const value = !todo.done;
        // Optimistic local update so the checkbox flips instantly.
        calendarStore.editArrayObjectLocal({
            field: 'todos', key: 'done', value, id: calendar.id, uuidv4: todo.uuidv4,
        });
        await calendarStore.editArrayObject({
            field: 'todos', key: 'done', value, type: 'Boolean', id: calendar.id, uuidv4: todo.uuidv4,
        });
    };

    startEdit = (todoUuidv4) => {
        this.setState({ editingUuidv4: todoUuidv4 });
    };

    stopEdit = () => {
        const { editingUuidv4 } = this.state;
        const { calendar } = this.props;
        // Flush any pending debounced save immediately when leaving edit mode.
        if (editingUuidv4 && this.updateTimers[editingUuidv4]) {
            clearTimeout(this.updateTimers[editingUuidv4]);
            delete this.updateTimers[editingUuidv4];
            const todo = (calendar?.todos || []).find(t => t.uuidv4 === editingUuidv4);
            if (todo) {
                this.saveText(editingUuidv4, todo.text || '');
            }
        }
        this.setState({ editingUuidv4: null });
    };

    handleTextChange = (todo, e) => {
        const { value } = e.target;
        const { calendar, stores } = this.props;
        const { calendarStore } = stores;
        const timerKey = todo.uuidv4;

        this.autoGrow(e.target);

        // Keep the controlled textarea responsive by updating the observable immediately.
        calendarStore.editArrayObjectLocal({
            field: 'todos', key: 'text', value, id: calendar.id, uuidv4: timerKey,
        });

        // Auto-save with a 750ms debounce (project array-object pattern).
        this.setUpdateInProgress(timerKey, true);
        if (this.updateTimers[timerKey]) {
            clearTimeout(this.updateTimers[timerKey]);
        }
        this.updateTimers[timerKey] = setTimeout(() => {
            this.saveText(timerKey, value);
        }, 750);
    };

    saveText = async (todoUuidv4, value) => {
        const { calendar, stores } = this.props;
        const { calendarStore } = stores;
        await calendarStore.editArrayObject({
            field: 'todos', key: 'text', value, type: 'String', id: calendar.id, uuidv4: todoUuidv4,
        });
        this.setUpdateInProgress(todoUuidv4, false);
    };

    removeTodo = async (todo) => {
        const { calendar, stores } = this.props;
        const { calendarStore } = stores;
        await calendarStore.removeArrayObject({
            field: 'todos', id: calendar.id, uuidv4: todo.uuidv4,
        });
    };

    openVisitorPicker = (todo) => {
        const { stores, pickerTitle } = this.props;
        const { appState } = stores;
        const level = (typeof appState.getTopMostDrawer === 'function' ? appState.getTopMostDrawer() : 0) + 1;
        // Use `onSelect` (not `callback`): the Drawer wrapper invokes its own `callback()` with no
        // args on close, which would crash a `({ visitor, animal })` destructure.
        appState.openDrawer('calendarTodoVisitorPicker', {
            height: appState.drawerHeightMedium,
            title: pickerTitle,
            onSelect: ({ visitor, animal }) => this.attachVisitorAnimal(todo, visitor, animal),
        }, level);
    };

    attachVisitorAnimal = async (todo, visitor, animal) => {
        const { calendar, stores } = this.props;
        const { calendarStore } = stores;
        if (!calendar?.id || !todo?.uuidv4) {
            return;
        }
        const args = { field: 'todos', id: calendar.id, uuidv4: todo.uuidv4 };
        // Optimistic local update so the chip appears instantly...
        calendarStore.editArrayObjectLocal({ ...args, key: 'visitor', value: visitor });
        calendarStore.editArrayObjectLocal({ ...args, key: 'animal', value: animal });
        // ...then persist. The generic todos PATCH path stores the nested object as-is
        // (util.cleanObject does not recurse), so no `type` is passed.
        await calendarStore.editArrayObject({ ...args, key: 'visitor', value: visitor });
        await calendarStore.editArrayObject({ ...args, key: 'animal', value: animal });
    };

    detachVisitorAnimal = async (todo) => {
        const { calendar, stores } = this.props;
        const { calendarStore } = stores;
        if (!calendar?.id || !todo?.uuidv4) {
            return;
        }
        const args = { field: 'todos', id: calendar.id, uuidv4: todo.uuidv4 };
        calendarStore.editArrayObjectLocal({ ...args, key: 'visitor', value: null });
        calendarStore.editArrayObjectLocal({ ...args, key: 'animal', value: null });
        // null round-trips through fetchApi (JSON.stringify) and clears the field ($set ... = null).
        await calendarStore.editArrayObject({ ...args, key: 'visitor', value: null });
        await calendarStore.editArrayObject({ ...args, key: 'animal', value: null });
    };

    goToSale = async (todo) => {
        const { stores } = this.props;
        const { saleStore, animalStore, userStore } = stores;
        const visitor = todo?.visitor;
        const animal = todo?.animal;
        if (!visitor || !visitor.id) {
            return;
        }
        // Build the same shapes a calendar booking produces (md5 identifies sale line items).
        const visitorForSale = { ...visitor, md5: md5(JSON.stringify(visitor)).toString() };
        const animalsForSale = animal && animal.id
            ? [{ ...animal, md5: md5(JSON.stringify(animal)).toString() }]
            : [];

        saleStore.createNewSale();
        saleStore.updateObjectKeyValue('newSale', 'visitors', [visitorForSale]);
        saleStore.updateObjectKeyValue('newSale', 'animals', animalsForSale);

        // Mirror createNewVisit: populate the sale page's visitor context.
        const visitorAnimals = await animalStore.load({ query: { owner: [visitor.id] }, skipUpdate: true });
        saleStore.updateKeyValue('visitorAnimals', visitorAnimals || []);
        await saleStore.loadVisitorSales([visitor.id], {
            query: { today: 1 },
            myCustomers: sharedSalesEnabled(userStore),
        });

        route('/dyrejournal-sale/');
    };

    render() {
        const { calendar, attachTitle, goToSaleTitle, detachTitle } = this.props;
        const { newTodoText, editingUuidv4, updateInProgress } = this.state;
        const todos = (calendar?.todos || []).filter(t => t && t.uuidv4);
        // Open items first, completed sorted to the bottom (stable within each group).
        const sortedTodos = [...todos].sort((a, b) => {
            if (!!a.done === !!b.done) {
                return 0;
            }
            return a.done ? 1 : -1;
        });

        return (
            <div class='calendar-todos w-100 px-2 py-1 mt-1 bg-body-tertiary rounded'>
                <div class='d-flex flex-row align-items-center text-body-secondary mb-1' style='font-size: 0.8em;'>
                    <i class='fa-duotone fa-list-check me-1' />
                    <Text id='calendar.todos.title'>Todo</Text>
                </div>

                {sortedTodos.map((todo) => {
                    const isEditing = editingUuidv4 === todo.uuidv4;
                    const hasVisitor = !!(todo.visitor && todo.visitor.id);
                    const hasAnimal = !!(todo.animal && todo.animal.id);
                    return (
                        <div key={todo.uuidv4} class='mb-1'>
                            <div class='d-flex flex-row align-items-start'>
                                <input
                                    type='checkbox'
                                    class='form-check-input flex-shrink-0 me-2 mt-1'
                                    checked={!!todo.done}
                                    onChange={() => this.toggleDone(todo)}
                                />
                                {isEditing ? (
                                    <textarea
                                        class='form-control form-control-sm px-1 flex-grow-1'
                                        style='min-width: 0; resize: none; overflow: hidden; line-height: 1.2;'
                                        value={todo.text || ''}
                                        ref={this.textareaRef}
                                        onInput={e => this.handleTextChange(todo, e)}
                                        onBlur={this.stopEdit}
                                    />
                                ) : (
                                    <div
                                        class={`flex-grow-1 px-1 ${todo.done ? 'text-decoration-line-through text-body-secondary' : ''}`}
                                        style='min-width: 0; white-space: pre-wrap; overflow-wrap: anywhere; cursor: text; line-height: 1.2; font-size: 0.9em;'
                                        onClick={() => this.startEdit(todo.uuidv4)}
                                    >
                                        {todo.text}
                                    </div>
                                )}
                                <button
                                    type='button'
                                    class='btn btn-sm btn-link text-body-secondary p-0 ms-1 mt-1 flex-shrink-0'
                                    onClick={() => this.openVisitorPicker(todo)}
                                    title={attachTitle}
                                >
                                    <i class={`fa-duotone ${hasVisitor ? 'fa-user-pen' : 'fa-user-plus'}`} />
                                </button>
                                {updateInProgress[todo.uuidv4]
                                    ? <span class='spinner-border spinner-border-sm text-body-secondary flex-shrink-0 ms-1 mt-1' role='status' aria-hidden='true' />
                                    : (
                                        <Localizer>
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-link text-body-secondary p-0 ms-1 mt-1 flex-shrink-0'
                                                onClick={() => this.removeTodo(todo)}
                                                title={<Text id='calendar.todos.delete'>Delete</Text>}
                                            >
                                                <i class='fa-duotone fa-xmark' />
                                            </button>
                                        </Localizer>
                                    )}
                            </div>
                            {hasVisitor ? (
                                <div class='d-flex flex-row align-items-center mt-1 ms-4'>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-link text-decoration-none p-0 text-start flex-grow-1'
                                        style='min-width: 0;'
                                        onClick={() => this.goToSale(todo)}
                                        title={goToSaleTitle}
                                    >
                                        <span class='badge bg-body-secondary text-body fw-normal text-wrap text-start' style='font-size: 0.85em;'>
                                            <i class='fa-duotone fa-user me-1' />
                                            {todo.visitor.firstname} {todo.visitor.lastname}
                                            {hasAnimal ? (
                                                <span class='ms-1'>
                                                    <i class='fa-duotone fa-paw ms-1 me-1' />
                                                    {todo.animal.name}
                                                </span>
                                            ) : null}
                                            <i class='fa-duotone fa-arrow-right-from-bracket ms-2' />
                                        </span>
                                    </button>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-link text-body-secondary p-0 ms-1 flex-shrink-0'
                                        onClick={() => this.detachVisitorAnimal(todo)}
                                        title={detachTitle}
                                    >
                                        <i class='fa-duotone fa-xmark' />
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    );
                })}

                <div class='d-flex flex-row align-items-center'>
                    <i class='fa-duotone fa-plus text-body-secondary flex-shrink-0 me-2' />
                    <Localizer>
                        <input
                            type='text'
                            class='form-control form-control-sm border-0 bg-transparent px-1'
                            placeholder={<Text id='calendar.todos.add-placeholder'>Add todo…</Text>}
                            value={newTodoText}
                            onInput={e => this.setState({ newTodoText: e.target.value })}
                            onKeyDown={this.handleNewTodoKeyDown}
                        />
                    </Localizer>
                </div>
            </div>
        );
    }
}

export default CalendarTodos;
